import {
  createContext,
  useContext,
  useEffect,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useAppState } from '@/ui/AppState';
import { DOCK_MAX_SIZE, DOCK_MIN_SIZE } from '@/constants/app';

/**
 * Every panel — the table, the calculator, the whiteboard, the library — is
 * docked by default: it takes its own strip of the screen, the shell lays
 * itself out in what is left, and the question stays readable and answerable
 * beside it. A panel you have to close to get back to the work is a panel you
 * stop opening.
 *
 * Expanded instead, it is the old behaviour: a sheet from the bottom of a
 * phone or a drawer from the right of a laptop, over the page behind a scrim,
 * for when the table wants the whole screen. The choice is one setting shared
 * by all of them, because only one is open at a time.
 */
/**
 * Forces a panel out of the dock regardless of the setting.
 *
 * A second panel opened while one is docked is shown over the page: it is a
 * glance at the table, not a change to the layout, and the docked panel must
 * still be there when it closes.
 */
const SheetOnly = createContext(false);

export function PanelAsSheet({ children }: { children: ReactNode }) {
  return <SheetOnly.Provider value={true}>{children}</SheetOnly.Provider>;
}

/** Whether what is being rendered right now sits in the dock. */
export function usePanelDocked(): boolean {
  const { local } = useAppState();
  const sheetOnly = useContext(SheetOnly);
  return !sheetOnly && (local?.panelsDocked ?? true);
}

export function Panel({
  title,
  onClose,
  actions,
  children,
}: {
  title: string;
  onClose: () => void;
  /** Extra controls for the header, before the dock and close buttons. */
  actions?: ReactNode;
  children: ReactNode;
}) {
  const { updateLocal } = useAppState();
  const docked = usePanelDocked();
  const sheetOnly = useContext(SheetOnly);

  useEffect(() => {
    // Docked, Escape belongs to whatever is being worked on, not to the panel.
    if (docked) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, docked]);

  return (
    <>
      {docked ? null : <button className="scrim" aria-label="Close panel" onClick={onClose} />}
      <aside
        className={`panel${docked ? ' panel--docked' : ''}`}
        role={docked ? 'region' : 'dialog'}
        aria-label={title}
      >
        {docked ? <DockHandle /> : null}
        <header className="panel__head">
          <h2>{title}</h2>
          <div className="row" style={{ gap: 'var(--sp-2)' }}>
            {actions}
            {sheetOnly ? null : (
              <button
                className="icon-btn"
                aria-pressed={!docked}
                aria-label={docked ? 'Make this full screen' : 'Keep this beside the page'}
                title={docked ? 'Make this full screen' : 'Keep this beside the page'}
                onClick={() => void updateLocal({ panelsDocked: !docked })}
              >
                {docked ? '⤢' : '⤡'}
              </button>
            )}
            <button className="icon-btn" onClick={onClose} aria-label="Close panel">
              ✕
            </button>
          </div>
        </header>
        <div className="panel__body">{children}</div>
      </aside>
    </>
  );
}

/**
 * The edge of a docked panel, dragged to resize it.
 *
 * The size is a fraction of the screen rather than pixels, so it means the
 * same thing on a phone and a laptop, and it is written to settings once the
 * drag ends rather than on every frame.
 */
function DockHandle() {
  const { updateLocal } = useAppState();

  let dragging = false;
  let latest: number | null = null;

  function sizeFrom(event: ReactPointerEvent<HTMLDivElement>): number {
    const wide = window.matchMedia('(min-width: 860px)').matches;
    const fraction = wide
      ? 1 - event.clientX / window.innerWidth
      : 1 - event.clientY / window.innerHeight;
    return Math.min(Math.max(fraction, DOCK_MIN_SIZE), DOCK_MAX_SIZE);
  }

  return (
    <div
      className="panel__handle"
      role="separator"
      aria-label="Resize this panel"
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        dragging = true;
      }}
      onPointerMove={(event) => {
        if (!dragging) return;
        latest = sizeFrom(event);
        // Set on the shell, where the variable lives, so the page gives up the
        // room as the edge moves rather than only once it is let go.
        event.currentTarget.closest('.app')?.setAttribute('style', `--dock-size: ${latest}`);
      }}
      onPointerUp={() => {
        if (!dragging) return;
        dragging = false;
        if (latest !== null) void updateLocal({ dockSize: latest });
      }}
      onPointerCancel={() => {
        dragging = false;
      }}
    />
  );
}
